import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { flatten } from 'lodash';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import ProductEnhancement, {
  productEnhancementCloneApi,
  productEnhancementDeleteApi,
  productEnhancementSaveApi,
} from 'models/productManagement/productEnhancement/Model.ProductEnhancement';
import {
  productEnhancementDocumentDeleteApi,
  productEnhancementDocumentSaveApi,
  productEnhancementDocumentUpdateAllFieldsApi,
  productEnhancementDocumentReplaceApi,
  productEnhancementDocumentOrderApi,
} from 'models/productManagement/productEnhancementDocument/ProductEnhancementDocument';
import { useEffect, useMemo, useState } from 'react';
import { productEnhancementIndexRoute } from './ProductEnhancement.Index';

export enum ProductEnhancementAction {
  EDIT = 'edit',
  DELETE = 'delete',
  CLONE = 'clone',
}
export enum ProductEnhancementEditTab {
  INFO = 'info',
  DOCUMENTS = 'documents',
}
export type VerifyProductAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductEnhancement.Actions,
  Utils.ValueOf<typeof ProductEnhancementAction>
>;
export type VerifyProductEnhancementEditTab = Utils.VerifyExtends<
  Module.ProductManagement.ProductEnhancement.EditTab,
  Utils.ValueOf<typeof ProductEnhancementEditTab>
>;
export default function useProductEnhancement() {
  const { permissions, user } = useAuthStore();

  const { lang } = getLocales();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductEnhancement.Params,
    Module.ProductManagement.ProductEnhancement.Query
  >({
    route: productEnhancementIndexRoute,
  });
  const productEnhancementId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;
  const [isLoading, setIsLoading] = useState(false);
  const canView =
    permissions.CREATE_PRODUCT ||
    permissions.EDIT_PRODUCT ||
    permissions.DELETE_PRODUCT ||
    permissions.VIEW_PRODUCTTIERPRICEPOINT ||
    permissions.CREATE_PRODUCTTIERPRICEPOINT ||
    permissions.EDIT_PRODUCTTIERPRICEPOINT ||
    permissions.DELETE_PRODUCTTIERPRICEPOINT;

  const listApi = useApi({
    action: ProductEnhancement.list,
    default: {
      limit: defaultLimit,
      offset: defaultOffset,
      dealerId: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ?? undefined,
    },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({ ...pageRouter.params }, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const {
    payload: productEnhancement,
    execute: reloadProductEnhancement,
    isExecuting: isEditLoading,
    reset: resetProductEnhancement,
  } = useApi(
    {
      action: ProductEnhancement.find,
      body: { id: productEnhancementId },
      default: null,
      wait: !productEnhancementId,
    },
    [productEnhancementId]
  );
  useEffect(() => {
    if (!productEnhancementId) resetProductEnhancement(true);
  }, [productEnhancementId]);

  const deleteApi = useApi({
    action: productEnhancementDeleteApi,
    callback: () => {
      listApi.execute((body) => body);
      pageRouter.redirect({ ...pageRouter.params, action: undefined }, { ...pageRouter.query, editTab: undefined });
    },
    wait: true,
  });

  const cloneApi = useApi({
    action: productEnhancementCloneApi,
    callback: (_, res) => {
      pageRouter.redirect(
        { id: res?.payload?.toString(), action: ProductEnhancementAction.EDIT },
        { ...pageRouter.query, editTab: ProductEnhancementEditTab.INFO }
      );
    },
    wait: true,
  });

  //#region Data editing
  const currentProductEnhancement = productEnhancement?.toJSON();
  const form = useForm<
    Utils.FormData<Model.IProductEnhancement> & {
      productEnhancementDocuments?: Array<
        Utils.FormData<Model.IProductEnhancementDocument> & { isFileRepalced?: boolean }
      >;
    }
  >(
    {
      default: currentProductEnhancement
        ? {
            ...currentProductEnhancement,
            productType: currentProductEnhancement?.productType ?? 'WARRANTY_TIER-BASED',
          }
        : {
            productType: 'WARRANTY_TIER-BASED',
            products: [],
          },
      onSubmit: async ({ productEnhancementDocuments, ...productEnhancement }) => {
        const productSaveResponse = await productEnhancementSaveApi(productEnhancement);
        const productEnhancementId = productSaveResponse.payload;
        if (productEnhancementDocuments?.length) {
          await productEnhancementDocumentUpdateAllFieldsApi({
            productEnhancementId: productEnhancement.id || productEnhancementId,
            productEnhancementAdditionalFields: flatten(
              productEnhancementDocuments.map((d) => d.productEnhancementAdditionalFields)
            ),
          });
          await productEnhancementDocumentOrderApi(
            productEnhancementDocuments?.reduce(
              (p, c) => ({
                ...p,
                [c.id]: c.orderNumber ?? 0,
              }),
              {}
            )
          );
        }
        for (const doc of productEnhancementDocuments ?? []) {
          if (doc.isFileRepalced && doc.formFile) {
            await productEnhancementDocumentReplaceApi({
              id: productEnhancement.id || productEnhancementId,
              documentId: doc.id,
              formFile: doc.formFile,
            });
          }
        }
        if (productEnhancementId?.toString() === pageRouter.params.id) {
          reloadProductEnhancement({ id: productEnhancementId });
        } else {
          pageRouter.updateParams({
            action: ProductEnhancementAction.EDIT,
            id: productEnhancementId.toString(),
          });
        }
      },
      validation: (data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.pricingDistributionTemplateId) errors.pricingDistributionTemplateId = lang.mustNotBeEmpty;
      },
    },
    [productEnhancement]
  );
  const newDocuments = form.data.productEnhancementDocuments?.filter((d) => !d.id) || [];
  const uploadedDocuments = form.data.productEnhancementDocuments?.filter((d) => !!d.id) || [];
  //#endregion
  function reload() {
    reloadProductEnhancement((body) => body);
  }
  async function uploadFile(params: Pick<Model.IProductDocument, 'formFile' | 'documentCategory' | 'orderNumber'>) {
    if (!params.formFile || !params.documentCategory) return;
    setIsLoading(true);
    await productEnhancementDocumentSaveApi({
      ...params,
      productEnhancementId: productEnhancement.id,
      languageIsoCode: Language.english,
    });
    setIsLoading(false);
    reload();
    return;
  }
  async function deleteFile(documentId: number) {
    if (!documentId) return;
    setIsLoading(true);
    const translatedDocs = await documentTranslationListApi({
      documentId: [documentId],
      documentType: 'ProductDocument',
    });
    for (const translatedDoc of translatedDocs.payload?.data ?? []) {
      await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
    }
    await productEnhancementDocumentDeleteApi({ id: form.data?.id, documentId });
    setIsLoading(false);
    reload();
  }

  return {
    reload,
    uploadFile,
    deleteFile,
    newDocuments,
    uploadedDocuments,
    productEnhancement,
    pageRouter,
    listApi,
    deleteApi,
    cloneApi,
    form,
    selected,
    permissions,
    isEditLoading: isEditLoading || isLoading,
    canView,
    views: {
      [ProductEnhancementAction.DELETE]:
        pageRouter.params.action === ProductEnhancementAction.DELETE && selected && permissions.DELETE_PRODUCT,
      [ProductEnhancementAction.CLONE]:
        pageRouter.params.action === ProductEnhancementAction.CLONE && selected && permissions.CREATE_PRODUCT,
      [ProductEnhancementAction.EDIT]:
        pageRouter.params.action === ProductEnhancementAction.EDIT &&
        ((permissions.CREATE_PRODUCT && !selected) || (permissions.EDIT_PRODUCT && selected)),
    },
  };
}

export function useProductEnhancementProvider() {
  return useDataProvider<ReturnType<typeof useProductEnhancement>>();
}
